import { useCallback, useRef, useState } from 'react';
import { inversionApi } from '../lib/api';
import { applyGeoRefToModel, buildGeoRef, previewGeoJSON } from '../lib/georef';
import { productToModel } from '../lib/inversion-parser';

/**
 * Georeferencing state for client-side inversion models (InversionPanel).
 *
 * One GeoRef per model (docs/GEOREFERENCING_AND_MODEL_IMPORT.md): `open(entry)`
 * shows the GeorefDialog for a model, `save(georef)` applies it — in the browser
 * when proj4 knows the CRS, else by re-parsing on the server with the georef —
 * then updates the model entry and adds an electrodes layer and a footprint layer
 * to the map. `fromDrawnLine` keeps the old flow (survey line drawn on the map).
 *
 *   onUpdateModel(entry)            replace a model entry (same id)
 *   onAddLayer(geojson, name, kind) add a result layer to the map
 *   onPersistError(message)         a stored model's PUT /georef failed (the browser copy keeps it)
 *
 * Stored models (entries with a `backendId`, V.12) also get the GeoRef saved on the
 * server with PUT /api/inversion/{id}/georef; the browser copy stays the display cache.
 */
/** [[west, south], [east, north]] of a georeferenced model entry (footprint, else cell positions), or null. */
export function modelBounds(entry) {
  let w = Infinity, so = Infinity, e = -Infinity, n = -Infinity;
  const add = (lon, lat) => {
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    w = Math.min(w, lon); e = Math.max(e, lon); so = Math.min(so, lat); n = Math.max(n, lat);
  };
  const g = entry?.footprint?.geometry;
  if (g?.coordinates) {
    const ring = g.type === 'Polygon' ? g.coordinates[0] : g.coordinates;
    (ring || []).forEach(([lon, lat]) => add(lon, lat));
  }
  if (!Number.isFinite(w)) (entry?.cells || []).forEach(c => add(c.lon, c.lat));
  return Number.isFinite(w) ? [[w, so], [e, n]] : null;
}

export default function useModelGeoref({ onUpdateModel, onAddLayer, onFitBounds, onPersistError }) {
  const [target, setTarget] = useState(null);
  const sources = useRef(new Map()); // entry id -> { text, fileName, iteration }

  const remember = useCallback((id, source) => { sources.current.set(id, source); }, []);
  const sourceOf = useCallback((id) => sources.current.get(id), []);
  const open = useCallback((entry) => setTarget(entry), []);
  const close = useCallback(() => setTarget(null), []);

  const addLayers = useCallback((entry) => {
    if (!onAddLayer || !entry.georef) return;
    const electrodes = entry.model.electrodes || [];
    if (electrodes.length) {
      onAddLayer(previewGeoJSON(electrodes, entry.georef), `Electrodes — ${entry.name}`, 'vector', { source: { kind: 'model', id: entry.id, name: entry.name } });
    }
    if (entry.footprint) {
      onAddLayer({ type: 'FeatureCollection', features: [entry.footprint] }, `Footprint — ${entry.name}`, 'vector', { source: { kind: 'model', id: entry.id, name: entry.name } });
    }
  }, [onAddLayer]);

  /** Apply a GeoRef to a model entry; returns the updated entry. `persist: false` skips the
   *  server save (the stored model already has this georef, e.g. inherited from its dataset). */
  const apply = useCallback(async (entry, georef, { persist = true } = {}) => {
    let geo;
    try {
      geo = applyGeoRefToModel(entry.model, georef);
    } catch (browserError) {
      // CRS unknown to proj4 in the browser: let the server (pyproj) do it.
      const src = sources.current.get(entry.id);
      if (!src) throw browserError;
      const res = await inversionApi.parse({ content: src.text, file_name: src.fileName, georef, iteration: src.iteration ?? null });
      const model = productToModel(res.data, src.fileName);
      geo = { cells: model.cells, electrodes: model.electrodes, line: res.data.line || null, crs: model.crs, georef: res.data.georef,
        footprint: res.data.footprint ? { type: 'Feature', properties: { kind: 'footprint' }, geometry: res.data.footprint } : null };
    }
    const updated = {
      ...entry,
      cells: geo.cells,
      line: geo.line,
      georef: geo.georef,
      footprint: geo.footprint,
      model: { ...entry.model, electrodes: geo.electrodes, crs: geo.crs, georef: geo.georef },
    };
    onUpdateModel(updated);
    addLayers(updated);
    if (persist && entry.backendId) {
      try {
        await inversionApi.setGeoref(entry.backendId, georef);
      } catch (err) {
        onPersistError?.(`Georeference not saved on the server (${err?.response?.data?.detail || err.message}); it is applied in this browser tab only.`);
      }
    }
    return updated;
  }, [onUpdateModel, addLayers, onPersistError]);

  const save = useCallback(async (georef) => {
    if (!target) return;
    const updated = await apply(target, georef);
    setTarget(null);
    const bounds = onFitBounds && modelBounds(updated);
    if (bounds) onFitBounds(bounds);
  }, [target, apply, onFitBounds]);

  /** Legacy flow: a line drawn on the map gives the [lon, lat] of the model x-range ends. */
  const fromDrawnLine = useCallback((model, lineFeature) => {
    const coords = lineFeature.geometry.coordinates;
    const [s, e] = [coords[0], coords[coords.length - 1]];
    return buildGeoRef({
      mode: 'tie_points', crs: 'EPSG:4326',
      start: { E: s[0], N: s[1] }, end: { E: e[0], N: e[1] },
      start_local: [model.xRange[0], 0], end_local: [model.xRange[1], 0],
    }, []);
  }, []);

  return { target, open, close, save, apply, fromDrawnLine, remember, sourceOf, addLayers };
}
